import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import useScrollPositionThrottled from '../../hooks/useScrollPositionThrottled';
import { getHashtagPosts } from '../../services/postService';

import MobileHeader from '../Header/MobileHeader/MobileHeader';
import TextButton from '../Button/TextButton/TextButton';
import PreviewImage from '../PreviewImage/PreviewImage';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import ImageGrid from '../ImageGrid/ImageGrid';
import { normalizeMediaType } from '../../utils/media';

const HashtagPosts = ({ token, showModal, showAlert }) => {
  const [posts, setPosts] = useState({
    posts: [],
    postCount: 0,
    fetching: false,
    hasMore: false,
  });

  const { hashtag } = useParams();
  const history = useHistory();

  const handlePostDialogDispatch = useCallback((action) => {
    if (!action || action.type !== 'SET_POST_VOTES_COUNT') {
      return;
    }
    const {
      postId,
      postVotes,
      voteCount,
      reactionType,
      likeCount: nextLikeCount,
    } = action.payload || {};
    if (!postId) {
      return;
    }
    setPosts((previous) => {
      const updatedPosts = previous.posts.map((post) => {
        const currentId =
          (post._id && post._id.toString()) ||
          (post.postId && post.postId.toString());
        if (currentId !== postId) {
          return post;
        }
        const hadReaction = post.reactionType === 'like';
        const hasReaction = reactionType === 'like';
        const incomingLikeCount =
          typeof nextLikeCount === 'number' ? nextLikeCount : null;
        let likeCount =
          incomingLikeCount !== null ? incomingLikeCount : post.likeCount || 0;
        if (incomingLikeCount === null && typeof reactionType !== 'undefined') {
          if (hasReaction && !hadReaction) {
            likeCount += 1;
          } else if (!hasReaction && hadReaction) {
            likeCount = Math.max(0, likeCount - 1);
          }
        }
        return {
          ...post,
          postVotes: Array.isArray(postVotes) ? postVotes : post.postVotes,
          postVotesCount:
            typeof voteCount === 'number' ? voteCount : post.postVotesCount,
          likeCount,
          reactionType:
            typeof reactionType !== 'undefined'
              ? reactionType
              : post.reactionType,
        };
      });
      return { ...previous, posts: updatedPosts };
    });
  }, []);

  const handleClick = (postId, avatar) => {
    if (window.outerWidth <= 600) {
      history.push(`/post/${postId}`);
    } else {
      showModal(
        {
          postId: postId.toString(),
          avatar,
          profileDispatch: handlePostDialogDispatch,
        },
        'PostDialog/PostDialog'
      );
    }
  };

  const retrievePosts = async (offset) => {
    try {
      setPosts((previous) => ({ ...previous, fetching: true }));
      const response = await getHashtagPosts(token, hashtag, offset);
      response.posts
        ? setPosts((previous) => ({
            posts: previous.posts
              ? [...previous.posts, ...response.posts]
              : response.posts,
            postCount: response.postCount,
            fetching: false,
            hasMore: response.length === 20,
          }))
        : setPosts((previous) => ({ ...previous, fetching: false }));
    } catch (err) {
      showAlert(err.message);
    }
  };

  const renderSkeleton = (amount) => {
    const skeleton = [];
    for (let i = 0; i < amount; i++) {
      skeleton.push(
        <SkeletonLoader key={i} style={{ minHeight: '30rem' }} animated />
      );
    }
    return skeleton;
  };

  useScrollPositionThrottled(
    ({ atBottom }) => {
      if (atBottom && posts.hasMore && !posts.fetching) {
        retrievePosts(posts.posts.length);
      }
    },
    null,
    [posts]
  );

  const retrievePostsRef = useRef(retrievePosts);

  useEffect(() => {
    retrievePostsRef.current();
  }, [retrievePostsRef]);

  const parseMetadata = (metadata) => {
    if (!metadata) {
      return null;
    }
    if (typeof metadata === 'object') {
      return metadata;
    }
    try {
      return JSON.parse(metadata);
    } catch (err) {
      return null;
    }
  };

  const getMediaProps = (post) => {
    const mediaUrl = post.mediaUrl || post.image || post.thumbnail;
    const thumbnail = post.thumbnail || mediaUrl;
    const mediaType = normalizeMediaType(post.mediaType, mediaUrl);
    return {
      mediaUrl,
      thumbnail,
      mediaType,
      filter: post.filter || '',
      metadata: parseMetadata(post.metadata),
    };
  };

  return !posts.fetching && posts.posts.length === 0 ? (
    <div className="hashtag-posts__empty">
      <h2 className="heading-2">
        Could not find any post associated with #{hashtag}.
      </h2>
    </div>
  ) : (
    <Fragment>
      <MobileHeader backArrow>
        <TextButton style={{ justifySelf: 'center' }} bold large>
          #{hashtag}
        </TextButton>
      </MobileHeader>
      <div className="hashtag-posts__title">
        <h2 className="heading-2">#{hashtag}</h2>
        <h3 className="heading-3 font-medium">
          <span className="font-bold">{posts.postCount}</span>{' '}
          {posts.postCount === 1 ? 'post' : 'posts'}
        </h3>
      </div>
      <ImageGrid>
        {posts.posts &&
          posts.posts.map((post, idx) => {
            const media = getMediaProps(post);
            const aggregatedReactions =
              (post.likeCount || 0) +
              (post.loveCount || 0) +
              (post.hahaCount || 0);
            let likesTotal = aggregatedReactions;
            if (likesTotal === 0) {
              if (typeof post.postVotesCount === 'number') {
                likesTotal = post.postVotesCount;
              } else if (Array.isArray(post.postVotes)) {
                likesTotal = post.postVotes.length;
              } else if (typeof post.postVotes === 'number') {
                likesTotal = post.postVotes;
              }
            }
            return (
              <PreviewImage
                key={idx}
                image={post.image}
                thumbnail={media.thumbnail}
                mediaUrl={media.mediaUrl}
                mediaType={media.mediaType}
                metadata={media.metadata}
                likes={likesTotal}
                comments={post.comments}
                filter={media.filter}
                onClick={() => handleClick(post._id, post.avatar)}
              />
            );
          })}
        {posts.fetching && renderSkeleton(10)}
      </ImageGrid>
    </Fragment>
  );
};

export default HashtagPosts;
